import React, { useState } from "react";
import PropTypes from "prop-types";
import { Grid, Typography, IconButton, Box } from "@mui/material";

import ChartCard from "ui-component/cards/ChartCard";
import { gridSpacing } from "store/constant";
import info from "../assets/images/icons/info.svg";
import share from "assets/images/icons/Share.svg";
import ToggleButtonComponent from "ui-component/ToggleButton";
import CustomChart from "./CustomChart";
import InfoDataTooltip  from "../component/ProjectDetailNavTabs/InfoDataTooltip";
import InfoDataTooltipAccordion from "../component/ProjectDetailNavTabs/InfoDataTooltipAccordion";

const ChartLayout = ({ tabDataName, projectName, chartData }) => {
  let isGroupChart = chartData.type === "group";

  if (
    isGroupChart 
    && (
      chartData.views.metrics.length === 0
    )
  ) {
    console.error(`The chart ${chartData.title} is declared as a group type, but its metrics list is empty. Falling back to a simple chart.`);
    isGroupChart = false;
  }

  const [selectedMetric, setSelectedMetric] = useState(
    chartData.views.metrics[0]?.value
  );
  const [selectedUnit, setSelectedUnit] = useState(chartData.views.units[0]);

  let seriesData = chartData.views.units.length
    ? selectedUnit.keys.reduce((acc, item) => {
        acc[item] = chartData.series_data[item];
        return acc;
      }, {})
    : !chartData.views.units.length && chartData.views.metrics.length
    ? chartData.series_data[selectedMetric]
    : chartData.series_data;



  let seriesDataKey = '';
  if (isGroupChart) {
    if (chartData.views.metrics.length && chartData.views.units.length) {
      seriesDataKey = `${selectedMetric}_${selectedUnit.value}`;
    } else if (!chartData.views.units.length && chartData.views.metrics.length) {
      seriesDataKey = selectedMetric;
    }
    seriesData = Object.entries(chartData.series_data).filter(
      ([key, value]) => {
        const regex = new RegExp(
          `(?:^|_)${selectedMetric}_(?:[A-Za-z]+_)?${selectedUnit.value}$`
        );
        return regex.test(key);
      }
    );
  }
  function replaceSpacesWithUnderscore(input) {
    return input.replace(/[ ,]+/g, "_");
  }

  if (
    seriesData === null ||
    seriesData === undefined ||
    (Array.isArray(seriesData) && seriesData.length === 0)
  ) {
     console.error(`The chart ${chartData.title} is misconfigured. If the chart is of group type, check the metrics and units lists. Te combination of {metric}_{unit} must be a key in the series_data object.`);
    return null;
  }

  function getMatchingData(keysObj, dataObj) {
    const matchingEntries = [];
  
    if (!keysObj || !Array.isArray(keysObj.keys) || !dataObj) {
      return matchingEntries;
    }
  
    keysObj.keys.forEach((key) => {
      if (dataObj[key]) {
        matchingEntries.push(dataObj[key]);
      }
    });
  
    return matchingEntries;
  }

  const isSingleResource = chartData.resources && !Array.isArray(chartData.resources) && typeof chartData.resources === 'object' && !Object.values(chartData.resources)[0]?.identifier;

  return (
    <ChartCard>
      <Grid container spacing={gridSpacing}>
        <Grid item xs={12}>
          <Grid>
            <Grid item>
              <Grid container direction="column" spacing={1}>
                {isGroupChart && (
                  <Grid pt={1} pb={2} pl={1} sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="h5" sx={{ color: "#2B2B2B" }}>
                        {seriesDataKey in chartData.series_data && "name" in
                        chartData.series_data[
                          seriesDataKey
                        ]
                          ? chartData.series_data[
                            seriesDataKey
                            ]?.name
                          : chartData.title}
                      </Typography>
                  </Grid>
                )}
                <Grid item xs={12} pb={4}>
                  <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    {isGroupChart && chartData.views.metrics.length > 1 ? (
                      <Grid item>
                        <ToggleButtonComponent
                          options={chartData.views.metrics}
                          value={selectedMetric}
                          onChange={(e) => setSelectedMetric(e.target.value)}
                          color="secondary"
                        />
                      </Grid>
                    ) : (
                      <Grid item sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="h5" sx={{ color: "#2B2B2B" }}>
                            {chartData.title}
                          </Typography>
                          {Object.keys(chartData.resources).length !== 0 && (
                              <Box sx={{ marginTop: "10px" }}>
                                {
                                  isSingleResource ?
                                  <InfoDataTooltip data={chartData} />
                                  :
                                  <InfoDataTooltipAccordion
                                  data={{
                                    ...chartData,
                                    resources: getMatchingData(selectedUnit, chartData.resources),
                                  }}
                                />
                                }
                              
                              </Box>
                            )}
                      </Grid>
                    )}
                    {chartData.views.units.length > 1 && (
                      <Grid item>
                        <ToggleButtonComponent
                          options={chartData.views.units}
                          value={selectedUnit.value}
                          onChange={(e) =>
                            setSelectedUnit(
                              chartData.views.units.find(
                                (m) => m.value === e.target.value
                              )
                            )
                          }
                          color="secondary"
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item display="none" alignItems="center">
              <IconButton>
                <img src={share} alt="share" />
              </IconButton>
              <IconButton>
                <img src={info} alt="info" />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <CustomChart
              chartData={seriesData}
              chartType={chartData.chart_type.replace("stacked_", "")}
              chartColors={chartData.colors}
              isStacked={chartData.chart_type.includes("stacked")}
              chartDescription={chartData.text}
              chartExportFileName={
                replaceSpacesWithUnderscore(projectName) +
                "_" +
                replaceSpacesWithUnderscore(tabDataName) +
                "_" +
                (isGroupChart
                  ? selectedMetric + "_" + selectedUnit.value
                  : chartData.title)
              }
              xLabel={chartData?.xlabel}
              yLabel={
                chartData.views.units.length
                  ? selectedUnit.label
                  : chartData?.ylabel_text
              }
              legend={chartData.legend}
              groupChart={isGroupChart}
            />
          </Grid>
        </Grid>
      </Grid>
    </ChartCard>
  );
};

ChartLayout.propTypes = {
  tabDataName: PropTypes.string,
  projectName: PropTypes.string,
  chartData: PropTypes.object,
};

export default ChartLayout;
